import React, { JSX, Suspense, useContext, useEffect, useRef, useState } from "react";
import { CancelTokenSource } from "axios";
import { TFunction, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { wrapPromise } from "go-core";
import { LoadingContainer } from "go-core/components/Loading";
import { FILTER_VALUE_SEPARATOR } from "go-list/core/components/Filter/services";
import ReportData from "go-report/reports";
import {
	getChartReport,
	getChartType,
	getLastMonthFilterBetween,
	getOrganizationIds,
	getOrganizationIdsFromReportFetchRequest,
	getReport,
	parseOrganizationIds,
} from "go-report/reports/services/report-service";
import { getSelectedSegmentForReportConfig } from "go-report/reports/services/segment-service";
import { ReportConfig } from "go-report/reports/services/types";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { selectUser } from "go-security/services/users/selectors";
import { UserState } from "go-security/services/users/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import { apiReport } from "../../../../../../../../services/Api/Report/apiReport";
import { ReportKdsUtils } from "../../../../../../../../utils/reports/ReportKdsUtils";
import ReportShareUtils from "../../../../../../../../utils/reports/ReportShareUtils";

interface ListState {
	resource?: Record<string, any>;
	user: UserState;
	setOrganizationsIds: (organizationIds: number[]) => void;
}

const listName = "ORDER_PREPARATION_STATUS_CHANGE_ORDER_REPORT";
const resourceType = "ORDER_PREPARATION_STATUS_CHANGE_ORDER_REPORT";
const type = "order_preparation_status_change";

const getKsdOrdersSelectedColumns = () => {
	const newSelectedColumns = [...ReportKdsUtils.getSelectedColumns()];
	newSelectedColumns.splice(3, 0, "average_receiving_time");

	return newSelectedColumns;
};

const getKsdOrdersColumns = (t: TFunction) => {
	const newColumns = [...ReportKdsUtils.getColumns(t)];
	newColumns.splice(3, 0, {
		id: "average_receiving_time",
		name: t("modules.kds_order_report.field.average_receiving_time.title"),
		...ReportKdsUtils.getConfigPropertiesForTimeColumns(t, "receiving_time", "receiving_time_count"),
	});
	newColumns.push({
		id: "average_acceptance_time",
		name: t("modules.kds_order_report.field.average_acceptance_time.title"),
		...ReportKdsUtils.getConfigPropertiesForTimeColumns(t, "acceptance_time", "acceptance_time_count"),
	});

	return newColumns;
};

const Report = ({ resource, user, setOrganizationsIds }: ListState) => {
	const filtersParams = useRef<string>("");
	const { t } = useTranslation();
	const organization = useSelector(selectOrganization);
	const segmentContext = useContext(SegmentContext);

	if (!resource) return null;

	const data = resource.read();

	let config = {
		reportConfigId: "kds_report_order",
		aggregatePrefix: "sales",
		type: "bill",
		chart: {
			title: t("modules.kds_order_report.header.report.title"),
			lineTitle: t("modules.kds_order_report.field.report_chart.title"),
			aggregate: "average_total_time",
			type: getChartType(),
			parseGroup: (group: string) => {
				if (group === "HOUR_OF_DAY") return "ORDER_CREATED_AT_HOUR_OF_DAY";
				if (group === "DAY_OF_MONTH") return "ORDER_CREATED_AT_DAY_OF_MONTH";
				if (group === "DATE") return "ORDER_CREATED_AT";
				if (group === "DAY_OF_WEEK") return "ORDER_CREATED_AT_DAY_OF_WEEK";
				if (group === "MONTH") return "ORDER_CREATED_AT_MONTH";
				return group;
			},
		},
		groups: [
			...ReportShareUtils.getGroupReportCommonGroups(t),
			...ReportKdsUtils.getKdsOrdersGroups(t),
			...ReportKdsUtils.getGroups(t),
		],
		externalSelectedFilters: [
			{
				id: "date_range",
				filterType: "date",
				value: getLastMonthFilterBetween().split("=")[1],
				condition: getLastMonthFilterBetween().split("=")[0],
				visible: true,
			},
			{
				id: "organization_ids",
				filterType: "list",
				condition: "e",
				value: getOrganizationIds(user.organizations?.map((x) => x.id).join(FILTER_VALUE_SEPARATOR)),
				visible: true,
			},
		],
		selectedColumns: [...getKsdOrdersSelectedColumns(), "average_acceptance_time"],
		selectedGroups: ["NONE"],
		groupsInfo: {
			...ReportKdsUtils.getGroupsInfo(t),
			...ReportKdsUtils.getKdsOrdersGroupsInfo(t),
		},
		showPercent: true,
		columns: [...getKsdOrdersColumns(t), ...ReportKdsUtils.getKdsOrdersColumns(t)],
		filters: [
			...ReportShareUtils.getFilters(t, user),
			...ReportKdsUtils.getKdsOrdersFilters(t, true, filtersParams, type),
			...ReportKdsUtils.getFilters(t, true, filtersParams, type),
		],
		selectedSegment: getSelectedSegmentForReportConfig(data.segments, "all"),
		segments: [
			{
				id: "all",
				name: t("common.word.all", { ns: "lib" }),
				slug: "all",
				groups: ["NONE"],
				columns: [...getKsdOrdersSelectedColumns(), "average_acceptance_time"],
				filters: [
					{
						filterId: "date_range",
						filterType: "date",
						value: getLastMonthFilterBetween().split("=")[1],
						condition: getLastMonthFilterBetween().split("=")[0],
					},
					{
						filterId: "organization_ids",
						filterType: "list",
						condition: "e",
						value: getOrganizationIds(user.organizations?.map((x) => x.id).join(FILTER_VALUE_SEPARATOR)),
					},
				],
			},
		],
		fetch: (params: Record<string, any> = {}, sourceToken: CancelTokenSource) => {
			setOrganizationsIds(getOrganizationIdsFromReportFetchRequest(params.f));
			filtersParams.current = params.f;

			return Promise.all([
				getReport(
					apiReport.getReportsCustom,
					params,
					getOrganizationIdsFromReportFetchRequest(params.f),
					config.filters,
					type,
					sourceToken,
					params.groups === "NONE"
				),
				getChartReport(
					apiReport.getReportsCustom,
					params,
					"NONE",
					getOrganizationIdsFromReportFetchRequest(params.f),
					config.filters,
					type,
					sourceToken
				),
			]);
		},
		fetchChartData: (params: Record<string, any> = {}, sourceToken: CancelTokenSource) => {
			return getChartReport(
				apiReport.getReportsCustom,
				params,
				"NONE",
				getOrganizationIdsFromReportFetchRequest(params.f),
				config.filters,
				type,
				sourceToken
			);
		},
		saveSegment: (segment: SegmentType) => {
			return segmentContext.save(listName, resourceType, segment);
		},
		exportConfig: {
			title: t("modules.kds_order_report.header.title"),
			filename: t("modules.kds_order_report.field.export_config_filename.title"),
			organization: `${organization.name}`,
			taxIdNo: organization?.more?.print_company_on_pdf ? organization.more?.company_tax_id_no : undefined,
			company: organization?.more?.print_company_on_pdf ? organization.more?.company_name : undefined,
			pdfOrientation: organization?.more?.pdf_orientation,
			pdfFontSize: organization?.more?.default_pdf_font_size?.toString(),
		},
	} as ReportConfig;
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		filterValues: data.filter_values,
	};

	return <ReportData config={config} />;
};

export const UserReportsKdsOrdersPage = (): JSX.Element => {
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContext = useContext(SegmentContext);
	const user = useSelector(selectUser);
	const [organizationsIds, setOrganizationsIds] = useState<number[]>(() =>
		parseOrganizationIds(user.organizations ?? [])
	);
	const organizationIdsAsString = organizationsIds.join();

	useEffect(() => {
		const params: Record<string, any> = {};
		if (organizationsIds?.length > 0) {
			params.organization_id = organizationIdsAsString;
		}

		setResource(wrapPromise(segmentContext.get(listName, resourceType, undefined, params)));
	}, [organizationIdsAsString]);

	return (
		<Suspense fallback={<LoadingContainer />}>
			<Report resource={resource} user={user} setOrganizationsIds={setOrganizationsIds} />
		</Suspense>
	);
};
